<template>
  <div v-if="props.article" class="article-detail">
    <!-- Title -->
    <h1 class="h1" v-html="props.article?.title" />

    <!-- Main gallery / image -->
    <DefaultArticlesArtObjectsGalleryPreview
      v-if="getMainGalleryImages().length > 0"
      :images="getMainGalleryImages()"
      :detail-url="getGalleryPath()"
    />

<!--    <DefaultArticlesArtObjectsGallery-->
<!--      v-if="getMainGalleryImages().length > 0"-->
<!--      :images="getMainGalleryImages()"-->
<!--    />-->
    <div
      v-else-if="getMainGalleryImages().length === 1"
      class="ao__aside"
    >
      <DefaultArticlesArtObjectsImage
        :image="getMainGalleryImages().at(0)"
      />
    </div>

    <DefaultArticlesArticleImage
      v-else-if="mainImage"
      :src="mainImage"
      :alt="props.article?.title || ''"
      :source="mainImageSource"
      :caption="mainImageCaption"
    />

    <!-- Author and date -->
    <div class="flex justify-between items-start flex-col sm:flex-row gap-5">
      <DefaultArticlesAuthorAndDateInfo
        :is-detail="true"
        :author="props.article?.simpleAuthor"
        :published="props.article?.publishedDate"
        link="/"
      />
      <!--                <DefaultArticlesDiscussionButton/>-->

      <!-- TTS - text to speech player -->
      <DefaultArticlesTextToSpeechButton
        :id="props.article?.id"
        :title="props.article?.title"
      />
    </div>



    <!-- PEREX -->
    <div
      v-if="props.article?.perex"
      class="ao__perex text-lg leading-7 text-left"
      v-html="props.article?.perex"
    />

    <!-- CNC Ads - below perex -->
    <DefaultAdsCncWrapper
      :position="UniFeAdsPosition.BELOW_PEREX"
      :page="props.cncAdsPage"
    />

    <!-- RATING -->
    <DefaultArticlesArtObjectsRating
      v-if="article?.ratingDecoded && article?.ratingDecoded.rating"
      :payload="article?.ratingDecoded"
    />

    <!-- Art Objects -->
    <template v-for="(artObject, index) in visibleArtObjects" :key="index">
      <div v-if="artObject.type === '__repeater__'" class="cnc_repeater_ad_position"></div>
      <DefaultArticlesArtObject
        v-else
        :type="artObject?.type"
        :payload="artObject"
        :parent-ident="`article-${props.article?.id}`"
      />
    </template>

    <!-- CNC Ads - under article (HeyFomo) -->
    <DefaultAdsCncWrapper
      :position="UniFeAdsPosition.UNDER_ARTICLE"
      :page="props.cncAdsPage"
    />

    <!-- Social and discussion -->
    <div class="flex justify-between">
      <DefaultShareSocialList/>
      <!--                  <DefaultArticlesDiscussionButton-->
      <!--                    :is-detail="true"-->
      <!--                  />-->
    </div>

    <!-- Keywords -->
    <div class="flex flex-wrap gap-3 mt-6 sm:mt-10 lg:mt-12">
      <NuxtLink
        v-for="keyword in articleKeywords"
        :key="keyword"
        class="articleid__filter-item"
        :to="getSearchResultPath(keyword)"
      >
        {{ keyword }}
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import {UniFeAdsPosition} from "~/libs/constants/UniFeAdsPosition"
import type {PropType} from "vue"
import type ArticleLocal from "~/libs/interfaces/ArticleLocal"
import {CncAdsPage} from "~/libs/constants/CncAdsPage"
import type AssetLocal from "~/libs/interfaces/AssetLocal"
import {useArticleTools} from "~/composables/useArticleTools"
import Default from "~/components/Default/Articles/ArtObjects/Default.vue";
import {TemplateList} from "~/libs/constants/TemplateList";
import {ArticleEmbedLimitConfig} from "~/config/ArticleEmbedLimitConfig";

const props = defineProps({
  article: {type: Object as PropType<ArticleLocal>, required: false},
  cncAdsPage: {type: String as PropType<CncAdsPage>, required: true},
})

const logger = useFrontendLogger()

const articleKeywords = computed(() =>
  props.article?.keywordList?.edges
    ?.map((edge) => edge?.node?.name)
    .filter((name): name is string => !!name) ?? []
)

const mainImage = computed(() => {
  return props.article?.previewAsset?.urlWithProtocol || props.article?.articleLinkAssetList?.edges?.at(0)?.node?.asset?.urlWithProtocol || null
})

const mainImageSource = computed(() => {
  return props.article?.previewAsset?.source || props.article?.articleLinkAssetList?.edges?.at(0)?.node?.asset?.source || null
})

const mainImageTitle = computed(() => {
  return props.article?.previewAsset?.title || props.article?.articleLinkAssetList?.edges?.at(0)?.node?.asset?.title || null
})

const mainImageCaption = computed(() => {
  return props.article?.previewAsset?.caption || props.article?.articleLinkAssetList?.edges?.at(0)?.node?.asset?.caption || null
})


const getGalleryPath = () => {
  return useArticleTools(props.article).getGalleryPath()
}

const getMainGalleryImages = (): AssetLocal[] => {
  const pathRegex = /^(http|https)/;
  const images: AssetLocal[] = []
  if (props.article?.articleLinkAssetList?.edges) {
    props.article?.articleLinkAssetList?.edges.forEach((image) => {
      if (image?.node?.asset?.urlWithProtocol && pathRegex.test(image?.node?.asset?.urlWithProtocol)) {
        images.push({
          urlWithProtocol: image.node?.asset?.urlWithProtocol,
          caption: image.node?.asset?.caption,
          title:  image.node?.asset?.title,
          source:  image.node?.asset?.source,
        })
      } else {
        logger.info('Article asset path is invalid', {
          ArticleId: `${props.article?.id}`,
          ArticlePath: `${props.article?.path}`,
          AssetId: image?.node?.id,
          AssetPath: image?.node?.asset?.urlWithProtocol,
        })
      }
    })
    // remove the first image when the first image is the same as the header photo
    if (mainImage.value && images.length > 0 && mainImage.value === images.at(0)?.urlWithProtocol) {
      images.shift()
    }
  }
  return images
}

const getSearchResultPath = (query: string) => {
  return useArticleTools().getSearchResultPath(query)
}

const route = useRoute()

// Destructured once: isHiddenArtObject runs inside the repeater loops, so building
// the composable per call would allocate its closures O(n) times per render.
const {
  isHiddenArtObject,
  limitRenderedEmbeds,
  countRenderableEmbeds,
  countNestedRenderableEmbeds,
  shouldReportCapOnce,
} = useArticleEmbeds()

// --- Repeater constants and helpers ---
const REPEATER_CHAR_INTERVAL = 500
const REPEATER_MIN_CHARS_TO_END = 2000

const headingType = 'heading'
const audioPlayerType = 'spotify_podcast'
const commercialType = 'cnc_html_box'
const proseBlockTypes = ['paragraph', 'div', 'factbox']

const stripHtmlForCharCount = (html: string): string => {
  return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
}

const getArtObjectTextLength = (obj: any): number => {
  const html = obj?.rendered || obj?.contentHTML || ''
  if (html && typeof html === 'string') {
    const stripped = stripHtmlForCharCount(html)
    if (stripped.length > 0) return stripped.length
  }
  if (obj?.content?.length) {
    let sum = 0
    for (const child of obj.content) {
      sum += getArtObjectTextLength(child)
      if (child?.attributes?.text) sum += stripHtmlForCharCount(child.attributes.text).length
      if (child?.attributes?.author) sum += stripHtmlForCharCount(child.attributes.author).length
    }
    return sum
  }
  if (obj?.attributes?.reference) {
    let sum = 0
    if (obj.attributes.reference.primaryText) {
      sum += stripHtmlForCharCount(obj.attributes.reference.primaryText).length
    }
    if (obj.attributes.reference.text) {
      try {
        const parsed = JSON.parse(obj.attributes.reference.text)
        sum += (parsed?.children?.[0]?.text || '').length
      } catch { /* ignore */ }
    }
    return sum
  }
  return 0
}

const isHidden = (obj: any): boolean => isHiddenArtObject(obj)

const insertRepeaterPlaceholders = (artObjects: any[]): any[] => {
  const n = artObjects.length
  const charLengths = artObjects.map(obj => isHidden(obj) ? 0 : getArtObjectTextLength(obj))

  const suffixSum = new Array(n + 1).fill(0)
  for (let i = n - 1; i >= 0; i--) {
    suffixSum[i] = suffixSum[i + 1] + charLengths[i]
  }

  const nextAudioIdx = new Array(n + 1).fill(-1)
  const nextTextIdx = new Array(n + 1).fill(-1)
  for (let i = n - 1; i >= 0; i--) {
    nextAudioIdx[i] = (!isHidden(artObjects[i]) && artObjects[i].type === audioPlayerType)
      ? i : nextAudioIdx[i + 1]
    nextTextIdx[i] = (!isHidden(artObjects[i]) && charLengths[i] > 0)
      ? i : nextTextIdx[i + 1]
  }

  const isAudioPlayerDirectlyAhead = (fromIndex: number): boolean => {
    const audioAt = nextAudioIdx[fromIndex]
    if (audioAt === -1) return false
    const textAt = nextTextIdx[fromIndex]
    return textAt === -1 || audioAt <= textAt
  }

  const result: any[] = []
  let charCount = 0
  let waitForParagraph = false

  for (let i = 0; i < n; i++) {
    const obj = artObjects[i]

    if (isHidden(obj)) {
      result.push(obj)
      continue
    }

    result.push(obj)
    charCount += charLengths[i]

    if (obj.type === headingType || obj.type === audioPlayerType) {
      waitForParagraph = true
      continue
    }
    if (obj.type === commercialType) {
      continue
    }

    if (charCount < REPEATER_CHAR_INTERVAL) continue

    if (waitForParagraph) {
      if (proseBlockTypes.includes(obj.type)) {
        waitForParagraph = false
      } else {
        continue
      }
    }

    if (suffixSum[i + 1] < REPEATER_MIN_CHARS_TO_END) continue
    if (isAudioPlayerDirectlyAhead(i + 1)) continue

    result.push({ type: '__repeater__' })
    charCount = 0
  }

  return result
}

// --- End repeater helpers ---

const visibleArtObjects = computed(() => {
  let artObjects: any[] = props.article?.jsonBodyDecoded || []
  const template = useUniWebTemplate().getTemplateListItem()

  // Poggers: filter cnc_html_box for seznam traffic
  if (template === TemplateList.POGGERS && (route.query.utm_source as string)?.toLowerCase() === 'seznam') {
    const filtered: any[] = []
    let skipNextParagraph = false
    for (const obj of artObjects) {
      if (obj.type === 'cnc_html_box') {
        skipNextParagraph = true
        continue
      }
      if (skipNextParagraph && obj.type === 'paragraph') {
        skipNextParagraph = false
        continue
      }
      skipNextParagraph = false
      filtered.push(obj)
    }
    artObjects = filtered
  }

  // Cap rendered embeds per template (HeyFomo: Seznam News Feed constraint).
  // Runs for every template — ArticleEmbedLimitConfig null is what disables it —
  // and must stay above the repeater branch, which measures the final content.
  artObjects = limitRenderedEmbeds(
    artObjects,
    template ? ArticleEmbedLimitConfig[template] : null,
    template,
  )

  // HeyFomo: insert repeater placeholders
  if (template !== TemplateList.HEYFOMO) return artObjects
  if (!useRuntimeConfig().public.adsEnabled) return artObjects

  return insertRepeaterPlaceholders(artObjects)
})

// Reports how much the embed cap actually removed, so the production impact is
// measurable, and warns if an embed ever shows up below the top level, where the
// cap cannot see it (see useArticleEmbeds().countNestedRenderableEmbeds).
//
// The whole callback returns immediately on the server: reporting is client-only,
// and without the early guard SSR would walk the article body on every render just
// to throw the result away.
//
// Watched rather than run in onMounted because the article can change under a
// mounted component: this component is rendered unkeyed from both article pages, so
// Vue may reuse the instance. In practice the page parents also gate it behind
// `v-if="articleStore.article"` and the store nulls the article between navigations
// (stores/useArticlesStore.ts), so a remount is what usually happens — the id-level
// dedupe in shouldReportCapOnce() covers both cases.
watch(
  () => props.article?.id,
  (articleId) => {
    if (!import.meta.client) return

    const template = useUniWebTemplate().getTemplateListItem()
    const limit = template ? ArticleEmbedLimitConfig[template] : null
    if (limit === null) return

    const artObjects = props.article?.jsonBodyDecoded || []
    const totalEmbeds = countRenderableEmbeds(artObjects, template)
    const nestedEmbeds = countNestedRenderableEmbeds(artObjects, template)

    if (totalEmbeds <= limit && nestedEmbeds === 0) return
    if (!shouldReportCapOnce(articleId ? `${articleId}` : undefined)) return

    // One event per article, whichever combination triggered it. Fields are split by
    // level on purpose: the cap only ever applies to top-level embeds, while nested
    // ones render in addition to it — folding them into one "rendered" number would
    // misreport both cases.
    const topLevelHidden = Math.max(totalEmbeds - limit, 0)
    const report = {
      ArticleId: `${props.article?.id}`,
      ArticlePath: `${props.article?.path}`,
      TopLevelEmbeds: totalEmbeds,
      TopLevelRendered: Math.min(totalEmbeds, limit),
      TopLevelHidden: topLevelHidden,
      NestedEmbedsRendered: nestedEmbeds,
      EmbedsRenderedTotal: Math.min(totalEmbeds, limit) + nestedEmbeds,
    }

    if (nestedEmbeds > 0) {
      logger.warn(
        topLevelHidden > 0
          ? 'Article embeds capped, nested embeds escaped the cap'
          : 'Nested embeds escaped the cap',
        report,
      )
      return
    }

    logger.info('Article embeds capped', report)
  },
  {immediate: true},
)
</script>
